/**
 * Google Analytics 4 (gtag.js) loader shared by panel and admin.
 *
 * The script is injected on first call, never from index.html, so the panel can
 * keep it unloaded until the visitor accepts analytics cookies. On the panel, page
 * views come from the stream's Enhanced Measurement ("page changes based on
 * browser history events"), which follows TanStack Router navigations. Admin
 * sends its own query-free page views instead (see `trackGtagPageView`).
 */

type Gtag = (...args: unknown[]) => void;

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: Gtag;
    }
}

// Analytics only. Ads storage and signals stay off: the consent dialog never asks for them.
const GRANTED = {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
} as const;

const DENIED = {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
} as const;

export function gtagScriptSrc(measurementId: string) {
    return `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
}

function gtag(): Gtag {
    if (!window.gtag) {
        window.dataLayer = window.dataLayer ?? [];
        window.gtag = function gtag() {
            // gtag.js reads the `arguments` object itself, not an array.
            // biome-ignore lint/complexity/noArguments: gtag.js contract
            window.dataLayer?.push(arguments);
        };
    }
    return window.gtag;
}

/** Mirrors `ga-disable-*` so callers can read it without casting `window`. gtag.js still reads the window flag. */
const disabledMeasurementIds = new Set<string>();

interface GtagScriptState {
    loaded: boolean;
    listeners: Set<() => void>;
}

const scriptStates = new Map<string, GtagScriptState>();
const scriptGenerations = new Map<string, number>();

function scriptState(measurementId: string): GtagScriptState {
    const existing = scriptStates.get(measurementId);
    if (existing) return existing;
    const created: GtagScriptState = { loaded: false, listeners: new Set() };
    scriptStates.set(measurementId, created);
    return created;
}

function setKillSwitch(measurementId: string, disabled: boolean) {
    if (disabled) disabledMeasurementIds.add(measurementId);
    else disabledMeasurementIds.delete(measurementId);
    (window as unknown as Record<string, unknown>)[`ga-disable-${measurementId}`] = disabled;
}

function isCollectionDisabled(measurementId: string) {
    return disabledMeasurementIds.has(measurementId);
}

function markScriptLoaded(measurementId: string) {
    const state = scriptState(measurementId);
    if (state.loaded) return;
    state.loaded = true;
    for (const listener of state.listeners) listener();
    state.listeners.clear();
}

/** Drops loader state. Tests use this; a real page reloads instead. */
export function resetGtagLoaderForTests() {
    disabledMeasurementIds.clear();
    scriptStates.clear();
    scriptGenerations.clear();
}

function currentScript(measurementId: string) {
    const src = gtagScriptSrc(measurementId);
    const found = document.querySelector(`script[src="${src}"]`);
    return found instanceof HTMLScriptElement ? found : null;
}

function nextScriptGeneration(measurementId: string) {
    const generation = (scriptGenerations.get(measurementId) ?? 0) + 1;
    scriptGenerations.set(measurementId, generation);
    return generation;
}

function watchScript(script: HTMLScriptElement, measurementId: string, generation: number) {
    script.addEventListener('load', () => {
        if (scriptGenerations.get(measurementId) !== generation) return;
        script.dataset.gtagLoaded = 'true';
        markScriptLoaded(measurementId);
    });
}

function appendGtagScript(measurementId: string) {
    const generation = nextScriptGeneration(measurementId);
    const script = document.createElement('script');
    script.async = true;
    script.src = gtagScriptSrc(measurementId);
    watchScript(script, measurementId, generation);
    document.head.appendChild(script);
}

/**
 * Runs `listener` once gtag.js has executed for `measurementId`. If it already
 * has, runs it now. The returned function cancels a pending listener.
 */
export function onGtagScriptLoad(measurementId: string, listener: () => void) {
    const state = scriptState(measurementId);
    if (state.loaded) {
        listener();
        return () => undefined;
    }
    state.listeners.add(listener);
    return () => {
        state.listeners.delete(listener);
    };
}

/**
 * Load gtag.js once and start collection for `measurementId`. Called again after
 * `disableGtag` (re-accept without a reload), it re-grants consent and records a
 * page view for the page the visitor is on, since the first one was already sent.
 */
export function loadGtag(measurementId: string, config: Record<string, unknown> = {}) {
    if (typeof window === 'undefined') return;
    const g = gtag();
    setKillSwitch(measurementId, false);

    const existing = currentScript(measurementId);
    if (existing) {
        g('consent', 'update', GRANTED);
        g('event', 'page_view', { send_to: measurementId });
        if (scriptState(measurementId).loaded || existing.dataset.gtagLoaded === 'true') {
            markScriptLoaded(measurementId);
            return;
        }
        // The tag is still in the document but never finished (blocked, failed, or hung).
        // A later accept has to inject a new one; the old load listener is ignored via generation.
        existing.remove();
    }

    g('consent', 'default', GRANTED);
    g('js', new Date());
    g('config', measurementId, {
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
        ...config,
    });
    appendGtagScript(measurementId);
}

/** Stop collection for `measurementId`. A loaded script can't be unloaded, so this disables it. */
export function disableGtag(measurementId: string) {
    if (typeof window === 'undefined') return;
    setKillSwitch(measurementId, true);
    window.gtag?.('consent', 'update', DENIED);
}

/** The page URL without its query string or hash, which can carry emails and usernames. */
export function pageLocationWithoutQuery(location: Pick<Location, 'origin' | 'pathname'> = window.location) {
    return `${location.origin}${location.pathname}`;
}

/**
 * Pin `page_location` to the query-free URL for every later hit. Pinning it with
 * `set` also covers hits gtag sends on its own (Enhanced Measurement), so a raw
 * `?q=` never reaches Google.
 */
export function pinGtagPageLocation() {
    if (typeof window === 'undefined' || !window.gtag) return null;
    const page_location = pageLocationWithoutQuery();
    window.gtag('set', { page_location });
    return page_location;
}

/** Re-pin the query-free URL after a navigation and send a page view for it. */
export function trackGtagPageView(measurementId: string) {
    const page_location = pinGtagPageLocation();
    if (!page_location) return;
    window.gtag?.('event', 'page_view', { send_to: measurementId, page_location, page_title: document.title });
}

/**
 * GA4 recommended `sign_up` event. No-ops until gtag.js has loaded and while
 * collection is disabled (cookie reject). Returns whether the hit was queued
 * for a script that will actually send it.
 */
export function trackGtagSignUp(measurementId: string, method: string) {
    if (typeof window === 'undefined' || !window.gtag) return false;
    if (!scriptState(measurementId).loaded || isCollectionDisabled(measurementId)) return false;
    window.gtag('event', 'sign_up', { send_to: measurementId, method });
    return true;
}
